import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { Product } from '@/api/products'
import { productsApi } from '@/api/products'
import type { ProductUnit } from '@/api/suppliers'
import { UNITS, suppliersApi } from '@/api/suppliers'
import { IconCheck } from '@/components/icons'
import { Alert, Button, Field } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { useProductsCache } from '@/features/products/cache'
import { toast } from '@/lib/toast'

const NEW_CATEGORY = '__new__'

const selectClass =
  'h-touch w-full rounded-xl border border-gray-300 bg-white px-3 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100'

/**
 * Νέο προϊόν ή αλλαγή υπάρχοντος, σε μία φόρμα.
 *
 * Όλα όσα χρειάζεται ο εστιάτορας είναι εδώ, χωρίς να φύγει από τη σελίδα:
 * νέα κατηγορία και νέος προμηθευτής γράφονται επιτόπου, μέσα στη φόρμα.
 */
export function ProductForm({
  product,
  initialCategoryId = null,
  onDone,
}: {
  /** Χωρίς προϊόν η φόρμα δημιουργεί νέο. */
  product?: Product
  initialCategoryId?: string | null
  onDone: () => void
}) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const applyProducts = useProductsCache()
  const queryClient = useQueryClient()

  const products = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.list,
  })
  const suppliers = useQuery({
    queryKey: ['suppliers', ''],
    queryFn: () => suppliersApi.list(),
  })

  const [name, setName] = useState(product?.name ?? '')
  const [unit, setUnit] = useState<ProductUnit>(product?.unit ?? 'piece')
  const [category, setCategory] = useState<string>(
    product ? (product.categoryId ?? '') : (initialCategoryId ?? ''),
  )
  const [newCategory, setNewCategory] = useState('')
  const [note, setNote] = useState(product?.note ?? '')
  const [supplierIds, setSupplierIds] = useState<string[]>([])
  const [problem, setProblem] = useState<string | null>(null)

  const toggleSupplier = (id: string) =>
    setSupplierIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )

  const save = useMutation({
    mutationFn: async () => {
      // Η νέα κατηγορία φτιάχνεται πρώτα· αν το προϊόν αποτύχει μετά, η
      // κατηγορία μένει και απλώς θα είναι διαθέσιμη στην επόμενη προσπάθεια.
      let categoryId: string | null = category || null
      if (category === NEW_CATEGORY) {
        const created = await productsApi.createCategory(newCategory.trim())
        categoryId = created.id
        setCategory(created.id)
        void queryClient.invalidateQueries({ queryKey: ['products'] })
      }
      const fields = { name: name.trim(), unit, categoryId, note: note.trim() }
      return product
        ? productsApi.update(product, fields)
        : productsApi.create({ ...fields, supplierIds })
    },
    onSuccess: (data) => {
      applyProducts(data)
      toast.success(product ? t('products.saved') : t('products.created'))
      onDone()
    },
  })

  const submit = () => {
    if (!name.trim()) return setProblem(t('products.fields.name'))
    if (category === NEW_CATEGORY && !newCategory.trim())
      return setProblem(t('products.fields.newCategoryName'))
    if (!product && supplierIds.length === 0) return setProblem(t('products.pickSupplier'))
    setProblem(null)
    save.mutate()
  }

  const available = (suppliers.data ?? []).filter((s) => s.connectionStatus !== 'blocked')

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
      noValidate
    >
      {problem && <Alert tone="warning">{problem}</Alert>}
      {save.isError && <Alert>{translateError(save.error)}</Alert>}

      <Field
        name="product-name"
        label={t('products.fields.name')}
        placeholder={t('products.fields.namePlaceholder')}
        value={name}
        autoFocus={!product}
        maxLength={200}
        onChange={(event) => setName(event.target.value)}
      />

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="product-unit" className="text-sm font-medium text-gray-700">
            {t('products.fields.unit')}
          </label>
          <select
            id="product-unit"
            value={unit}
            onChange={(event) => setUnit(event.target.value as ProductUnit)}
            className={selectClass}
          >
            {UNITS.map((value) => (
              <option key={value} value={value}>
                {t(`unitNames.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="product-category" className="text-sm font-medium text-gray-700">
            {t('products.fields.category')}
          </label>
          <select
            id="product-category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className={selectClass}
          >
            <option value="">{t('products.fields.noCategory')}</option>
            {products.data?.categories.map((value) => (
              <option key={value.id} value={value.id}>
                {value.name}
              </option>
            ))}
            <option value={NEW_CATEGORY}>{t('products.fields.newCategory')}</option>
          </select>
        </div>
      </div>

      {category === NEW_CATEGORY && (
        <Field
          name="product-new-category"
          label={t('products.fields.newCategoryName')}
          placeholder={t('products.fields.newCategoryPlaceholder')}
          value={newCategory}
          autoFocus
          maxLength={100}
          onChange={(event) => setNewCategory(event.target.value)}
        />
      )}

      {!product && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-gray-700">
            {t('products.fields.suppliers')}
          </span>
          <div className="flex flex-wrap gap-2">
            {available.map((supplier) => {
              const selected = supplierIds.includes(supplier.id)
              return (
                <button
                  key={supplier.id}
                  type="button"
                  onClick={() => toggleSupplier(supplier.id)}
                  aria-pressed={selected}
                  className={
                    'flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold transition-colors ' +
                    (selected
                      ? 'border-brand-600 bg-brand-50 text-brand-800'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400')
                  }
                >
                  {selected && <IconCheck size={15} />}
                  {supplier.name}
                </button>
              )
            })}
          </div>
          <p className="text-xs text-gray-500">{t('products.fields.suppliersHint')}</p>
          <QuickSupplier onCreated={(id) => setSupplierIds((current) => [...current, id])} />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="product-note" className="text-sm font-medium text-gray-700">
          {t('products.fields.note')}
        </label>
        <textarea
          id="product-note"
          value={note}
          maxLength={500}
          rows={2}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('products.fields.notePlaceholder')}
          className="rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending}>
          {product ? t('common.save') : t('products.add')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

/**
 * Νέος προμηθευτής μέσα στη φόρμα του προϊόντος.
 *
 * Ο εστιάτορας που γράφει «γάλα» και θυμάται ότι το φέρνει κάποιος καινούργιος
 * δεν πρέπει να εγκαταλείψει τη φόρμα για να τον καταχωρίσει πρώτα.
 */
export function QuickSupplier({ onCreated }: { onCreated: (supplierId: string) => void }) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const create = useMutation({
    mutationFn: () =>
      suppliersApi.create({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
      }),
    onSuccess: (supplier) => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      onCreated(supplier.id)
      setOpen(false)
      setName('')
      setEmail('')
      setPhone('')
    },
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start rounded-lg px-1 py-2 text-sm font-semibold text-brand-700 hover:text-brand-600"
      >
        {t('products.newSupplier')}
      </button>
    )
  }

  // Δεν είναι <form>: βρίσκεται μέσα στη φόρμα του προϊόντος και ένθετες
  // φόρμες δεν επιτρέπονται στην HTML.
  return (
    <div className="mt-1 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
      <p className="text-sm font-semibold text-gray-900">{t('products.newSupplierTitle')}</p>
      {create.isError && <Alert>{translateError(create.error)}</Alert>}
      <Field
        name="quick-supplier-name"
        label={t('suppliers.fields.name')}
        value={name}
        autoFocus
        onChange={(event) => setName(event.target.value)}
      />
      <Field
        name="quick-supplier-email"
        label={t('suppliers.fields.email')}
        type="email"
        inputMode="email"
        hint={t('suppliers.emailIsIdentity')}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Field
        name="quick-supplier-phone"
        label={t('suppliers.fields.phone')}
        type="tel"
        inputMode="tel"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          loading={create.isPending}
          disabled={name.trim().length < 2 || !email.includes('@')}
          onClick={() => create.mutate()}
        >
          {t('suppliers.add')}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

/**
 * Δεύτερος προμηθευτής για υπάρχον προϊόν, από όπου κι αν το ζητήσει ο χρήστης.
 *
 * Το `productKey` είναι το ίδιο κλειδί ομαδοποίησης που χρησιμοποιεί η λίστα
 * αγορών, οπότε η λίστα ανοίγει αυτό το φύλλο χωρίς να ξέρει τίποτα άλλο.
 */
export function SupplierAdder({
  productKey,
  onAdded,
}: {
  productKey: string
  onAdded: (supplierId: string) => void
}) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const applyProducts = useProductsCache()

  const products = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.list,
  })
  const suppliers = useQuery({
    queryKey: ['suppliers', ''],
    queryFn: () => suppliersApi.list(),
  })
  const product = products.data?.products.find((value) => value.key === productKey)

  const add = useMutation({
    mutationFn: (supplierId: string) => productsApi.addSupplier(product!, supplierId),
    onSuccess: (data, supplierId) => {
      applyProducts(data)
      onAdded(supplierId)
    },
  })

  if (products.isPending || suppliers.isPending) {
    return <p className="text-sm text-gray-500">{t('common.loading')}</p>
  }
  if (!product) return <Alert>{t('errors.not_found')}</Alert>

  const others = (suppliers.data ?? []).filter(
    (supplier) =>
      supplier.connectionStatus !== 'blocked' &&
      !product.suppliers.some((s) => s.supplierId === supplier.id),
  )

  return (
    <div className="flex flex-col gap-3">
      {add.isError && <Alert>{translateError(add.error)}</Alert>}
      {others.length === 0 && (
        <p className="text-sm text-gray-500">{t('products.noOtherSuppliers')}</p>
      )}
      <ul className="flex flex-col gap-1.5">
        {others.map((supplier) => (
          <li key={supplier.id}>
            <button
              onClick={() => add.mutate(supplier.id)}
              disabled={add.isPending}
              className="flex min-h-12 w-full items-center rounded-xl border border-gray-200 px-4 text-left font-medium text-gray-900 hover:border-brand-400 hover:bg-brand-50"
            >
              {supplier.name}
            </button>
          </li>
        ))}
      </ul>
      <QuickSupplier onCreated={(supplierId) => add.mutate(supplierId)} />
    </div>
  )
}
